import { Injectable, Signal, computed, signal } from '@angular/core';
import { log } from '@jepto/shared/utils/logger';
// fallow-ignore-file unused-class-member
import { TableData } from '../models/chart-data.model';
import { UlidService } from './ulid.service';
import {
	CardError,
	CardErrorCode,
	CardErrorSeverity,
	ChartDataResponse,
	ImageDataRefs,
	type QueryCardDataResponse,
} from '../models/card-model';
import type { Analysis } from '../models/card-model';
import {
	isDimensionFilterCardData,
	type FilterCardRuntimeData,
} from '../models/page-filter.model';
import { sanitizeImageCardRefs } from '../utils/card-image-ref.util';

/**
 * Core service for handling data operations and real-time updates.
 *
 * Provides:
 * - Data fetching from AppSync GraphQL
 * - WebSocket connection for real-time updates
 * - Integration with filter and date range services
 * - Signal-based state management for reactive updates
 *
 * Used by components to:
 * - Fetch and query data
 * - React to real-time updates
 * - Apply filters and date ranges to data
 * - Handle data transformations
 */
@Injectable({
	providedIn: 'root',
})
export class DataService {
	// Primary signals for data state - stores WebSocket response data
	private cardDataMapSignal = signal<Map<string, QueryCardDataResponse>>(
		new Map<string, QueryCardDataResponse>()
	);
	private tableDataMapSignal = signal<Map<string, TableData>>(
		new Map<string, TableData>()
	);
	private tableDataSignals = new Map<string, Signal<TableData | undefined>>();

	constructor(private ulidService: UlidService) {}

	/** Returns true when an incoming requestId is stale relative to the stored one. */
	private isStaleRequestIdUpdate(
		cardId: string,
		dataLabel: string,
		currentRequestId: string | undefined,
		incomingRequestId: string | undefined
	): boolean {
		if (!currentRequestId || !incomingRequestId) {
			return false;
		}
		const comparison = this.ulidService.compare(
			incomingRequestId,
			currentRequestId
		);
		if (comparison <= 0) {
			log(
				`[DataService] Rejecting stale ${dataLabel} for ${cardId}: incoming requestId ${incomingRequestId} is older than current ${currentRequestId}`
			);
			return true;
		}
		return false;
	}

	/**
	 * Whether `data` carries renderable content. Table/columnar payloads must be
	 * checked before `series: []` — empty arrays are "falsy" for charts but are
	 * invalid for classifying table rows.
	 */
	private queryCardDataHasContent(data: unknown): boolean {
		if (data == null || typeof data !== 'object') {
			return false;
		}
		const d = data as Record<string, unknown>;
		if (d.result && typeof d.result === 'object' && d.result !== null) {
			const r = d.result as Record<string, unknown>;
			if (Array.isArray(r.columnDefs) && r.columnDefs.length > 0) {
				return true;
			}
			if (
				r.columns &&
				typeof r.columns === 'object' &&
				Object.keys(r.columns as object).length > 0
			) {
				return true;
			}
		}
		if (Array.isArray(d.columnDefs) && d.columnDefs.length > 0) {
			return true;
		}
		if (
			d.columns &&
			typeof d.columns === 'object' &&
			Object.keys(d.columns).length > 0
		) {
			return true;
		}
		if (Array.isArray(d.series) && d.series.length > 0) {
			return true;
		}
		if (Array.isArray(d.rowData) && d.rowData.length > 0) {
			return true;
		}
		if (
			d.donutSeries &&
			Array.isArray(d.donutSeries) &&
			d.donutSeries.length > 0
		) {
			return true;
		}
		return Object.keys(d).length > 0;
	}

	/**
	 * Updates query card data (chart or table envelope) from WebSocket or GQL.
	 * @param data The new {@link QueryCardDataResponse} payload
	 */
	updateCardData(data: Partial<QueryCardDataResponse>): void {
		if (data && data.cardId) {
			this.updateCardDataInMap(
				data.cardId,
				data as QueryCardDataResponse
			);
		}
	}

	/**
	 * Updates one card's entry in {@link cardDataMapSignal} without affecting other cards.
	 */
	private updateCardDataInMap(
		cardId: string,
		data: QueryCardDataResponse
	): void {
		const currentMap = this.cardDataMapSignal();
		const currentData = currentMap.get(cardId);

		// Check if we should accept this update based on requestId timestamps
		if (currentData?.requestId && data.requestId) {
			// Use UlidService to compare timestamps
			const comparison = this.ulidService.compare(
				data.requestId,
				currentData.requestId
			);

			if (comparison <= 0) {
				log(
					`[DataService] Rejecting stale query card data for ${cardId}: incoming requestId ${data.requestId} is older than current ${currentData.requestId}`
				);
				return;
			}
		}

		// If we have valid data and the incoming data is empty/null, only accept if it has a newer requestId
		// This prevents blank WebSocket messages from wiping out valid data
		const incomingHasData = this.queryCardDataHasContent(data.data);
		const currentHasData = this.queryCardDataHasContent(currentData?.data);

		// If incoming data is empty but current has data, require a requestId to overwrite
		if (!incomingHasData && currentHasData && !data.requestId) {
			console.warn(
				`[DataService] Rejecting empty query card data for ${cardId}: no requestId provided and would overwrite existing data`
			);
			return;
		}

		// Log data updates for debugging
		log(`[DataService] Updating query card data for ${cardId}:`, {
			hasIncomingData: incomingHasData,
			hadPreviousData: currentHasData,
			incomingRequestId: data.requestId,
			previousRequestId: currentData?.requestId,
			incomingSeriesLength: data.data?.series?.length,
			incomingRowDataLength: data.data?.rowData?.length,
			incomingDataKeys: data.data ? Object.keys(data.data) : [],
		});

		// Apply the update
		const newMap = new Map(currentMap);
		newMap.set(cardId, data);
		this.cardDataMapSignal.set(newMap);
	}

	/**
	 * Updates table data with new data
	 * @param cardId The card ID to update
	 * @param data The new table data
	 */
	updateTableData(cardId: string, data: TableData): void {
		if (!cardId) return;
		const newMap = new Map(this.tableDataMapSignal());
		newMap.set(cardId, data);
		this.tableDataMapSignal.set(newMap);
	}

	/**
	 * Gets a reactive signal for table data by card ID.
	 *
	 * @param cardId The ID of the card to get data for
	 * @returns A Signal that reactively updates when table data changes
	 */
	public tableDataSignal(cardId: string): Signal<TableData | undefined> {
		let sig = this.tableDataSignals.get(cardId);
		if (sig) {
			return sig;
		}
		sig = computed(() => this.tableDataMapSignal().get(cardId));
		this.tableDataSignals.set(cardId, sig);
		return sig;
	}

	/**
	 * Clears all table data and resets the signal cache.
	 */
	public clearAllTableData(): void {
		this.tableDataSignals.clear();
		this.tableDataMapSignal.set(new Map<string, TableData>());
	}

	/**
	 * Gets chart data for a specific card by its ID (snapshot value).
	 * @deprecated Use cardDataSignal(cardId) for reactive access in effects/computed.
	 * @param cardId The ID of the card to get data for
	 * @returns The chart data response for the specified card or null if not found
	 */
	public getQueryCardDataById(cardId: string): QueryCardDataResponse | null {
		return this.cardDataMapSignal().get(cardId) ?? null;
	}

	/**
	 * Cache of computed signals for chart data by card ID.
	 * This ensures we return the same signal instance for the same cardId,
	 * preventing unnecessary effect re-subscriptions.
	 */
	private cardDataSignals = new Map<
		string,
		Signal<QueryCardDataResponse | undefined>
	>();

	/**
	 * Reactive signal for query card data (chart or table) by card ID.
	 *
	 * Preferred for effects/computed when reading the shared WebSocket/GQL envelope.
	 * The signal is cached per cardId, so calling this multiple times with the same ID
	 * returns the same signal instance.
	 *
	 * Angular 21 best practice: Use signals for all reactive state access.
	 *
	 * @param cardId The ID of the card to get data for
	 * @returns A Signal that reactively updates when query card data changes
	 */
	public cardDataSignal(
		cardId: string
	): Signal<QueryCardDataResponse | undefined> {
		let sig = this.cardDataSignals.get(cardId);
		if (sig) {
			return sig;
		}
		// Create a computed signal that extracts this card's data from the map
		sig = computed(() => this.cardDataMapSignal().get(cardId));
		this.cardDataSignals.set(cardId, sig);
		return sig;
	}

	/**
	 * Clears all query card data and resets the signal cache.
	 * Call this when loading a new view/report.
	 */
	public clearAllCardData(): void {
		this.cardDataSignals.clear();
		this.cardDataMapSignal.set(new Map<string, QueryCardDataResponse>());
	}

	// ============================================
	// Content Data (HTML/Markdown cards)
	// ============================================

	/**
	 * Primary signal for content data state - stores WebSocket response data with metadata
	 */
	private contentDataMapSignal = signal<
		Map<string, { content: string; requestId?: string }>
	>(new Map<string, { content: string; requestId?: string }>());

	/**
	 * Cache of computed signals for content data by card ID.
	 */
	private contentDataSignals = new Map<string, Signal<string | undefined>>();

	/**
	 * Updates content data for HTML/Markdown cards from WebSocket.
	 * @param cardId The card ID to update
	 * @param content The content string (HTML or Markdown)
	 * @param requestId Optional ULID for ordering messages
	 */
	public updateContentData(
		cardId: string,
		content: string,
		requestId?: string
	): void {
		if (!cardId || content === undefined) return;

		const currentMap = this.contentDataMapSignal();
		const currentEntry = currentMap.get(cardId);

		if (
			this.isStaleRequestIdUpdate(
				cardId,
				'content data',
				currentEntry?.requestId,
				requestId
			)
		) {
			return;
		}

		// If we have valid content and incoming is empty, require a requestId to overwrite
		// This prevents blank WebSocket messages from wiping out valid content
		const hasCurrentContent =
			currentEntry?.content && currentEntry.content.trim().length > 0;
		const hasIncomingContent = content && content.trim().length > 0;

		// If incoming content is empty but current has content, require a requestId to overwrite
		if (!hasIncomingContent && hasCurrentContent && !requestId) {
			console.warn(
				`[DataService] Rejecting empty content data for ${cardId}: no requestId provided and would overwrite existing content`
			);
			return;
		}

		// Log content updates for debugging
		log(`[DataService] Updating content data for ${cardId}:`, {
			hasIncomingContent,
			incomingLength: content?.length || 0,
			incomingRequestId: requestId,
			previousRequestId: currentEntry?.requestId,
			contentChanged: currentEntry?.content !== content,
			contentPreview: content?.substring(0, 50) || 'EMPTY',
			previousContentPreview:
				currentEntry?.content?.substring(0, 50) || 'NONE',
		});

		const newMap = new Map(currentMap);
		newMap.set(cardId, { content, requestId });
		this.contentDataMapSignal.set(newMap);
	}

	/**
	 * Gets a reactive signal for content data by card ID.
	 * Use this in components to reactively display HTML/Markdown content.
	 *
	 * @param cardId The ID of the card to get content for
	 * @returns A Signal that reactively updates when content changes
	 */
	public contentDataSignal(cardId: string): Signal<string | undefined> {
		let sig = this.contentDataSignals.get(cardId);
		if (sig) {
			return sig;
		}
		sig = computed(() => this.contentDataMapSignal().get(cardId)?.content);
		this.contentDataSignals.set(cardId, sig);
		return sig;
	}

	/**
	 * Gets content data for a specific card (snapshot value).
	 * @param cardId The ID of the card
	 * @returns The content string or undefined
	 */
	public getContentDataById(cardId: string): string | undefined {
		return this.contentDataMapSignal().get(cardId)?.content;
	}

	/**
	 * Clears all content data and resets the signal cache.
	 */
	public clearAllContentData(): void {
		this.contentDataSignals.clear();
		this.contentDataMapSignal.set(
			new Map<string, { content: string; requestId?: string }>()
		);
	}

	// ============================================
	// Analysis card data (hydrated periods from WebSocket)
	// ============================================

	private analysesDataMapSignal = signal<
		Map<string, { analyses: Analysis[]; requestId?: string }>
	>(new Map());

	private analysesDataSignals = new Map<
		string,
		Signal<Analysis[] | undefined>
	>();

	public updateAnalysesData(
		cardId: string,
		analyses: Analysis[],
		requestId?: string
	): boolean {
		if (!cardId || !Array.isArray(analyses)) return false;

		const currentMap = this.analysesDataMapSignal();
		const currentEntry = currentMap.get(cardId);

		if (
			this.isStaleRequestIdUpdate(
				cardId,
				'analysis data',
				currentEntry?.requestId,
				requestId
			)
		) {
			return false;
		}

		const hasCurrent =
			currentEntry?.analyses && currentEntry.analyses.length > 0;
		const hasIncoming = analyses.length > 0;
		if (!hasIncoming && hasCurrent && !requestId) {
			console.warn(
				`[DataService] Rejecting empty analysis data for ${cardId}: no requestId and would overwrite existing analyses`
			);
			return false;
		}

		const newMap = new Map(currentMap);
		newMap.set(cardId, { analyses, requestId });
		this.analysesDataMapSignal.set(newMap);
		return true;
	}

	/** Replaces one card's committed analyses and resets WebSocket ordering. */
	public replaceAnalysesData(cardId: string, analyses: Analysis[]): void {
		if (!cardId || !Array.isArray(analyses)) return;

		const newMap = new Map(this.analysesDataMapSignal());
		newMap.set(cardId, { analyses });
		this.analysesDataMapSignal.set(newMap);
	}

	public analysesDataSignal(cardId: string): Signal<Analysis[] | undefined> {
		let sig = this.analysesDataSignals.get(cardId);
		if (sig) {
			return sig;
		}
		sig = computed(
			() => this.analysesDataMapSignal().get(cardId)?.analyses
		);
		this.analysesDataSignals.set(cardId, sig);
		return sig;
	}

	public clearAllAnalysesData(): void {
		this.analysesDataSignals.clear();
		this.analysesDataMapSignal.set(new Map());
	}

	// ============================================
	// Filter Data (Filter cards)
	// ============================================

	/**
	 * Primary signal for filter data state - stores WebSocket response data with metadata
	 */
	private filterDataMapSignal = signal<
		Map<string, { data: FilterCardRuntimeData; requestId?: string }>
	>(new Map<string, { data: FilterCardRuntimeData; requestId?: string }>());

	/**
	 * Cache of computed signals for filter data by card ID.
	 */
	private filterDataSignals = new Map<
		string,
		Signal<FilterCardRuntimeData | undefined>
	>();

	/**
	 * Current filter dimension member the UI expects for each card.
	 * Late WebSocket payloads from a previous Field selection must not overwrite
	 * options after a fast dimension switch (JEP-10140).
	 */
	private expectedFilterDimensionMemberByCardId = new Map<string, string>();

	/**
	 * Sets (or clears) the dimension member that WS filter payloads must match.
	 */
	public setExpectedFilterDimensionMember(
		cardId: string,
		member: string | null | undefined
	): void {
		if (!cardId) {
			return;
		}
		const trimmed = member?.trim() ?? '';
		if (!trimmed) {
			this.expectedFilterDimensionMemberByCardId.delete(cardId);
			return;
		}
		this.expectedFilterDimensionMemberByCardId.set(cardId, trimmed);
	}

	/**
	 * Updates filter data from WebSocket.
	 * @param cardId The card ID to update
	 * @param data The filter configuration data
	 * @param requestId Optional ULID for ordering messages
	 * @param options.replaceSelected Apply incoming `selected` even for WS updates
	 *   (default keeps session selection when `requestId` is present)
	 */
	public updateFilterData(
		cardId: string,
		data: FilterCardRuntimeData,
		requestId?: string,
		options?: { replaceSelected?: boolean }
	): void {
		if (!cardId || data === undefined) return;

		const currentMap = this.filterDataMapSignal();
		const currentEntry = currentMap.get(cardId);

		if (
			this.isStaleRequestIdUpdate(
				cardId,
				'filter data',
				currentEntry?.requestId,
				requestId
			)
		) {
			return;
		}

		// WS / getCardData responses only — local session updates omit requestId.
		if (requestId) {
			const expected =
				this.expectedFilterDimensionMemberByCardId
					.get(cardId)
					?.trim() ?? '';
			const controlType = (data as { controlType?: unknown }).controlType;
			const isDropdownShaped = controlType === 'dropdown';

			// Null/missing dimension is never valid option-list data. It must not
			// bypass the expected-member gate (isDimensionFilterCardData is false
			// when dimension is null) and wipe the store (JEP-10140).
			if (isDropdownShaped && !isDimensionFilterCardData(data)) {
				log(
					`[DataService] Rejecting filter data for ${cardId}: null/invalid dimension` +
						(expected ? ` while expecting ${expected}` : ''),
					{ requestId }
				);
				return;
			}

			if (isDimensionFilterCardData(data) && expected) {
				const incoming = data.dimension.member?.trim() ?? '';
				if (incoming !== expected) {
					log(
						`[DataService] Rejecting filter data for ${cardId}: dimension ${incoming} does not match expected ${expected}`,
						{ requestId }
					);
					return;
				}
			}
		}

		// WebSocket option lists re-send default selected=all. Local session
		// selection updates call this without requestId and must win.
		let nextData = data;
		const currentData = currentEntry?.data;
		if (
			requestId &&
			!options?.replaceSelected &&
			isDimensionFilterCardData(data) &&
			isDimensionFilterCardData(currentData)
		) {
			const optionValues = new Set(data.options.map(o => o.value));
			nextData = {
				...data,
				selected: currentData.selected.filter(v => optionValues.has(v)),
			};
		}

		// Log filter data updates for debugging
		log(`[DataService] Updating filter data for ${cardId}:`, {
			hasData: !!nextData,
			dataKeys: nextData ? Object.keys(nextData) : [],
			requestId,
		});

		const newMap = new Map(currentMap);
		newMap.set(cardId, { data: nextData, requestId });
		this.filterDataMapSignal.set(newMap);
	}

	/**
	 * Gets a reactive signal for filter data by card ID.
	 * Use this in filter components to reactively access filter configuration.
	 *
	 * @param cardId The ID of the card to get filter data for
	 * @returns A Signal that reactively updates when filter data changes
	 */
	public filterDataSignal(
		cardId: string
	): Signal<FilterCardRuntimeData | undefined> {
		let sig = this.filterDataSignals.get(cardId);
		if (sig) {
			return sig;
		}
		sig = computed(() => this.filterDataMapSignal().get(cardId)?.data);
		this.filterDataSignals.set(cardId, sig);
		return sig;
	}

	/**
	 * Gets filter data for a specific card (snapshot value).
	 * @param cardId The ID of the card
	 * @returns The filter data or undefined
	 */
	public getFilterDataById(
		cardId: string
	): FilterCardRuntimeData | undefined {
		return this.filterDataMapSignal().get(cardId)?.data;
	}

	public clearFilterData(cardId: string): void {
		if (!cardId) {
			return;
		}
		const currentMap = this.filterDataMapSignal();
		if (!currentMap.has(cardId)) {
			return;
		}
		const newMap = new Map(currentMap);
		newMap.delete(cardId);
		this.filterDataMapSignal.set(newMap);
	}

	/**
	 * Clears all filter data and resets the signal cache.
	 */
	public clearAllFilterData(): void {
		this.filterDataSignals.clear();
		this.expectedFilterDimensionMemberByCardId.clear();
		this.filterDataMapSignal.set(
			new Map<
				string,
				{ data: FilterCardRuntimeData; requestId?: string }
			>()
		);
	}

	// ============================================
	// Image Data (Image cards)
	// ============================================

	/**
	 * Shape of image data stored per card. All refs are optional because a
	 * WebSocket event may only update a subset of them.
	 */
	private imageDataMapSignal = signal<
		Map<string, ImageDataRefs & { requestId?: string }>
	>(new Map());

	private imageDataSignals = new Map<
		string,
		Signal<ImageDataRefs | undefined>
	>();

	/**
	 * Updates image ref data for an image card from a WebSocket event.
	 * @param cardId The card ID to update
	 * @param data Object containing imageRef, imageRefLight, imageRefDark (all optional)
	 * @param requestId Optional ULID for ordering messages
	 */
	public updateImageData(
		cardId: string,
		data: ImageDataRefs,
		requestId?: string
	): void {
		if (!cardId) return;

		const currentMap = this.imageDataMapSignal();
		const currentEntry = currentMap.get(cardId);

		if (
			this.isStaleRequestIdUpdate(
				cardId,
				'image data',
				currentEntry?.requestId,
				requestId
			)
		) {
			return;
		}

		const sanitized = sanitizeImageCardRefs(data);

		log(`[DataService] Updating image data for ${cardId}:`, {
			...sanitized,
			requestId,
		});

		const newMap = new Map(currentMap);
		newMap.set(cardId, { ...sanitized, requestId });
		this.imageDataMapSignal.set(newMap);
	}

	/**
	 * Gets a reactive signal for image data by card ID.
	 * Use this in JeptoImageCardComponent to reactively reflect WebSocket-delivered image refs.
	 *
	 * @param cardId The ID of the card to get image data for
	 * @returns A Signal that reactively updates when image data changes
	 */
	public imageDataSignal(cardId: string): Signal<ImageDataRefs | undefined> {
		let sig = this.imageDataSignals.get(cardId);
		if (sig) return sig;
		sig = computed(() => {
			const entry = this.imageDataMapSignal().get(cardId);
			if (!entry) return undefined;
			const { requestId: _r, ...refs } = entry;
			return refs;
		});
		this.imageDataSignals.set(cardId, sig);
		return sig;
	}

	/**
	 * Clears all image data and resets the signal cache.
	 */
	public clearAllImageData(): void {
		this.imageDataSignals.clear();
		this.imageDataMapSignal.set(new Map());
	}

	/**
	 * Clears all data (query cards, tables, content, filter, image) and resets all signal caches.
	 * Call this when loading a completely new view/report.
	 */
	public clearAllData(): void {
		this.clearAllCardData();
		this.clearAllTableData();
		this.clearAllContentData();
		this.clearAllAnalysesData();
		this.clearAllFilterData();
		this.clearAllImageData();
	}

	/** Sets an error on the query card data envelope for a card. */
	public setCardDataError(cardId: string, error: CardError): void {
		const currentMap = this.cardDataMapSignal();
		const currentData = currentMap.get(cardId);

		const newMap = new Map(currentMap);
		if (currentData) {
			// Add error to existing data
			newMap.set(cardId, { ...currentData, error });
		} else {
			// Create minimal entry with error
			newMap.set(cardId, { cardId, error } as QueryCardDataResponse);
		}
		this.cardDataMapSignal.set(newMap);
		console.error(`[DataService] Error set for card ${cardId}:`, error);
	}

	/** Clears the error on the query card data envelope for a card. */
	public clearCardDataError(cardId: string): void {
		const currentMap = this.cardDataMapSignal();
		const currentData = currentMap.get(cardId);

		if (currentData?.error) {
			const { error, ...rest } = currentData;
			const newMap = new Map(currentMap);
			newMap.set(cardId, rest as QueryCardDataResponse);
			this.cardDataMapSignal.set(newMap);
			log(`[DataService] Error cleared for card ${cardId}`);
		}
	}

	/**
	 * Creates a structured CardError object
	 * @param code Error code
	 * @param message User-friendly message
	 * @param options Additional error options
	 * @returns CardError object
	 */
	public createCardError(
		code: CardErrorCode,
		message: string,
		options?: {
			severity?: CardErrorSeverity;
			details?: string;
			originalError?: unknown;
			requestId?: string;
			executionId?: string;
			context?: Record<string, unknown>;
			recoverable?: boolean;
			suggestedAction?: string;
		}
	): CardError {
		return {
			code,
			message,
			severity: options?.severity ?? CardErrorSeverity.Error,
			timestamp: new Date().toISOString(),
			details: options?.details,
			originalError: options?.originalError,
			requestId: options?.requestId,
			executionId: options?.executionId,
			context: options?.context,
			recoverable: options?.recoverable ?? false,
			suggestedAction: options?.suggestedAction,
			stack:
				options?.originalError instanceof Error
					? options.originalError.stack
					: undefined,
		};
	}
}
